import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  NM_COLOR_SCHEME_STORAGE_KEY,
  broadcastResolvedTheme,
  colorSchemeFlatSurface,
  readStoredColorScheme,
  resolveToResolvedTheme
} from "../utils/nmThemeRuntime.js";
export { NM_COLOR_SCHEME_STORAGE_KEY } from "../utils/nmThemeRuntime.js";
const ThemeContext = createContext({
  colorScheme: "light",
  resolvedTheme: "light",
  setColorScheme: () => {},
  themeLocked: false
});
function syncDataTheme(resolvedTheme, flatSurface = null) {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-nm-resolved", resolvedTheme);
    if (flatSurface === "white" || flatSurface === "black") {
      document.documentElement.setAttribute("data-nm-flat-surface", flatSurface);
    } else {
      document.documentElement.removeAttribute("data-nm-flat-surface");
    }
  }
  for (const id of ["nitromule-react-root", "admin-dashboard-root", "marketing-root", "site-form-root"]) {
    const el = document.getElementById(id);
    if (!el) continue;
    el.setAttribute("data-theme", resolvedTheme);
    if (flatSurface === "white" || flatSurface === "black") {
      el.setAttribute("data-nm-flat-surface", flatSurface);
    } else {
      el.removeAttribute("data-nm-flat-surface");
    }
  }
}
export function ThemeProvider({
  children,
  forceResolvedTheme = null,
  flatSurface = null
}) {
  const lockedFlatSurface = flatSurface === "white" || flatSurface === "black" ? flatSurface : null;
  const [colorScheme, setColorSchemeState] = useState(readStoredColorScheme);
  const [systemIsDark, setSystemIsDark] = useState(() => typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)").matches : true);
  const resolvedTheme = useMemo(() => {
    if (forceResolvedTheme === "light" || forceResolvedTheme === "dark") return forceResolvedTheme;
    return resolveToResolvedTheme(colorScheme, systemIsDark);
  }, [forceResolvedTheme, colorScheme, systemIsDark]);
  const activeFlatSurface = useMemo(() => {
    if (lockedFlatSurface) return lockedFlatSurface;
    return colorSchemeFlatSurface(colorScheme);
  }, [lockedFlatSurface, colorScheme]);
  const themeLocked = forceResolvedTheme === "light" || forceResolvedTheme === "dark";
  useEffect(() => {
    if (themeLocked) return undefined;
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemIsDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [themeLocked]);
  useLayoutEffect(() => {
    syncDataTheme(resolvedTheme, activeFlatSurface);
    broadcastResolvedTheme(resolvedTheme);
  }, [resolvedTheme, activeFlatSurface]);
  const setColorScheme = useCallback(next => {
    if (themeLocked) return;
    if (next !== "system" && next !== "light" && next !== "dark" && next !== "black") return;
    setColorSchemeState(next);
    try {
      localStorage.setItem(NM_COLOR_SCHEME_STORAGE_KEY, next);
    } catch (_) {}
    const systemIsDarkNow =
      typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)").matches : true;
    const immediate = resolveToResolvedTheme(next, systemIsDarkNow);
    syncDataTheme(immediate, colorSchemeFlatSurface(next));
    broadcastResolvedTheme(immediate);
  }, [themeLocked]);
  const value = useMemo(() => ({
    colorScheme: themeLocked ? forceResolvedTheme : colorScheme,
    resolvedTheme,
    setColorScheme,
    themeLocked
  }), [themeLocked, forceResolvedTheme, colorScheme, resolvedTheme, setColorScheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
export function useTheme() {
  return useContext(ThemeContext);
}
export default ThemeContext;
